import { useCallback, useState } from "react";
import {
	TOOLBOX_INSERT_BATCH_EVENT,
	type ToolboxInsertBatchEventDetail,
} from "../../components/toolbox/insertHandler";
import { generate } from "./generator";
import type { LoremDocSettings } from "./settings";
import { useLoremSettings } from "./useLoremSettings";

export type LoremFireSurface = "chip" | "seed" | "header" | "slash";

/**
 * Telemetry event emitted every time a lorem trigger fires. Downstream
 * analytics can subscribe to a single event regardless of which UI
 * surface the click came from.
 */
export const LOREM_FIRED_EVENT = "lilia:lorem-fired";

export interface LoremFiredEventDetail {
	surface: LoremFireSurface;
	source: LoremDocSettings["source"];
	count: number;
	/** True when a slash override was clamped to LOREM_COUNT_MAX. */
	clamped?: boolean;
}

export interface UseLoremDispatchResult {
	isPending: boolean;
	settings: LoremDocSettings;
	/**
	 * Materialize defaults, generate paragraphs, dispatch the batch
	 * insert event, emit the telemetry event. Returns the resolved
	 * settings actually used (after Q1.5 lazy materialization).
	 */
	fire: () => Promise<LoremDocSettings>;
}

/**
 * Shared dispatch logic for the three lorem surfaces (chip / seed /
 * header). Encapsulates: useLoremSettings(docId), pending state during
 * async generate, batch event dispatch, and telemetry emission.
 *
 * Extracting this into a hook beats duplicating the same handleClick
 * + isPending pattern across three components — and gives one place to
 * wire telemetry sinks in T04 polish.
 */
export function useLoremDispatch(
	docId: string,
	surface: LoremFireSurface,
): UseLoremDispatchResult {
	const { settings, ensureMaterialized } = useLoremSettings(docId);
	const [isPending, setIsPending] = useState(false);

	const fire = useCallback(async (): Promise<LoremDocSettings> => {
		setIsPending(true);
		try {
			const resolved = ensureMaterialized();
			const paragraphs = await generate(resolved.source, resolved.count);
			const insertDetail: ToolboxInsertBatchEventDetail = {
				calls: paragraphs.map((text) => ({
					type: "paragraph",
					content: { text },
				})),
				source: "lorem",
			};
			window.dispatchEvent(
				new CustomEvent(TOOLBOX_INSERT_BATCH_EVENT, { detail: insertDetail }),
			);
			const fireDetail: LoremFiredEventDetail = {
				surface,
				source: resolved.source,
				count: resolved.count,
			};
			window.dispatchEvent(
				new CustomEvent(LOREM_FIRED_EVENT, { detail: fireDetail }),
			);
			return resolved;
		} finally {
			setIsPending(false);
		}
	}, [ensureMaterialized, surface]);

	return { isPending, settings, fire };
}
